import { IOperation } from '@/contracts/system-modules/sync/operation/IOperation'
import { IConflictState, IFoldNote, INoteState } from '@/contracts/modules/note/use-case/IFoldNote'
import { EnumNoteEvent } from '@/contracts/modules/note/enum/EnumNoteEvent'
import { INoteFileRecord } from '@/contracts/modules/note/entities/INoteFileRecord'

/** Узел дерева версий содержимого */
interface IVersionNode {
  time: string
  content: string
  parents: string[]
  isResolution: boolean
}

function compareTime(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Шифротекст переносится как есть; не строка (устаревшая заметка) — пустая строка */
function asCipher(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter((item): item is string => typeof item === 'string')
}

function asTimeList(value: unknown): string[] {
  return asStringList(value).sort(compareTime)
}

/** Событие без UUID файла или с размерами не числом — испорченное, пропускается */
function asFile(fields: Record<string, unknown>): INoteFileRecord | null {
  if (typeof fields.file !== 'string' || !Number.isInteger(fields.size) || !Number.isInteger(fields.chunk)) {
    return null
  }
  return { file: fields.file, size: fields.size as number, chunk: fields.chunk as number, key: asCipher(fields.key), meta: asCipher(fields.meta) }
}

/**
 * Свёртка событий одной заметки. События — факты в порядке меток: повтор по метке
 * пропускается, порядок доставки значения не имеет, потому что дерево версий строится по
 * ссылкам base и bases, а не по очереди прихода. Название и содержимое — шифротекст: свёртка
 * их не разбирает. Ключ заметки — из первого по метке события created, где он есть. Файлы:
 * file_attached добавляет файл, files_removed убирает; файл, который уже встречался, второй
 * раз не добавляется.
 */
export class FoldNote implements IFoldNote {
  exec(operations: IOperation[]): INoteState {
    const byTime = new Map<string, IOperation>()
    operations.forEach((operation) => byTime.set(operation.time, operation))
    const ordered = [...byTime.values()].sort((a, b) => compareTime(a.time, b.time))

    const state: INoteState = {
      key: null,
      title: '',
      content: '',
      isRemoved: false,
      heads: [],
      conflict: null,
      files: [],
      updatedAt: null,
    }
    const nodes = new Map<string, IVersionNode>()
    const files = new Map<string, INoteFileRecord>()
    const seenFiles = new Set<string>()

    ordered.forEach((operation) => {
      state.updatedAt = operation.time
      switch (operation.event) {
      case EnumNoteEvent.CREATED:
        if (state.key === null && typeof operation.fields.key === 'string') {
          state.key = operation.fields.key
        }
        state.title = asCipher(operation.fields.title)
        break
      case EnumNoteEvent.RENAMED:
        state.title = asCipher(operation.fields.title)
        break
      case EnumNoteEvent.REMOVED:
        state.isRemoved = true
        break
      case EnumNoteEvent.RESTORED:
        state.isRemoved = false
        break
      case EnumNoteEvent.CONTENT_CHANGED: {
        const base = operation.fields.base
        nodes.set(operation.time, {
          time: operation.time,
          content: asCipher(operation.fields.content),
          parents: typeof base === 'string' ? [base] : [],
          isResolution: false,
        })
        break
      }
      case EnumNoteEvent.CONFLICT_RESOLVED:
        nodes.set(operation.time, {
          time: operation.time,
          content: asCipher(operation.fields.content),
          parents: asTimeList(operation.fields.bases),
          isResolution: true,
        })
        break
      case EnumNoteEvent.FILE_ATTACHED: {
        const file = asFile(operation.fields)
        if (file !== null && !seenFiles.has(file.file)) {
          seenFiles.add(file.file)
          files.set(file.file, file)
        }
        break
      }
      case EnumNoteEvent.FILES_REMOVED:
        asStringList(operation.fields.files).forEach((file) => files.delete(file))
        break
      }
    })
    state.files = [...files.values()]

    this.linkSameResolutions(nodes)
    const heads = this.heads(nodes)
    state.heads = heads.map((node) => node.time)
    if (heads.length > 0) {
      state.content = heads[heads.length - 1].content
    }
    if (heads.length > 1) {
      state.conflict = this.conflict(nodes, heads[heads.length - 2], heads[heads.length - 1])
    }

    return state
  }

  /** Два разрешения одного конфликта: позднее считается потомком раннего, побеждает большая метка */
  private linkSameResolutions(nodes: Map<string, IVersionNode>): void {
    const resolutions = [...nodes.values()].filter((node) => node.isResolution)
    resolutions.forEach((node) => {
      const key = node.parents.join(',')
      resolutions
        .filter((other) => other.time < node.time && other.parents.join(',') === key)
        .forEach((other) => node.parents.push(other.time))
    })
  }

  private heads(nodes: Map<string, IVersionNode>): IVersionNode[] {
    const hasChild = new Set<string>()
    nodes.forEach((node) => node.parents.forEach((parent) => hasChild.add(parent)))

    return [...nodes.values()]
      .filter((node) => !hasChild.has(node.time))
      .sort((a, b) => compareTime(a.time, b.time))
  }

  private conflict(nodes: Map<string, IVersionNode>, left: IVersionNode, right: IVersionNode): IConflictState {
    const common = [...this.ancestors(nodes, left)].filter((time) => this.ancestors(nodes, right).has(time))
    const base = common.sort(compareTime).pop()

    return {
      left: { time: left.time, content: left.content },
      right: { time: right.time, content: right.content },
      base: base === undefined ? null : (nodes.get(base)?.content ?? null),
    }
  }

  private ancestors(nodes: Map<string, IVersionNode>, node: IVersionNode): Set<string> {
    const seen = new Set<string>()
    const queue = [...node.parents]
    while (queue.length > 0) {
      const time = queue.shift() as string
      if (seen.has(time) || !nodes.has(time)) {
        continue
      }
      seen.add(time)
      queue.push(...(nodes.get(time) as IVersionNode).parents)
    }
    return seen
  }
}
